import Phaser from 'phaser';
import { ENEMY, ENEMY_CHASE, ELITE, ELITE_SHOOTER, ELITE_BOMBER, ELITE_GUARDIAN, BOSS, BOSS_TYPES, GAME_HEIGHT, type BossTypeKey } from '../config/GameConfig';

export interface EnemyOptions {
  hp?: number;
  width?: number;
  height?: number;
  score?: number;
  isBoss?: boolean;
  bossType?: BossTypeKey;
  isMiniBoss?: boolean;
  /** 是否为精英怪（紫色 tint + 体型放大 + 高 HP） */
  isElite?: boolean;
  /** 是否为射手精英（橙红 tint + 主动开火） */
  isShooter?: boolean;
  /** 是否为自爆精英（红色 tint + 锁定引爆） */
  isBomber?: boolean;
  /** 是否为护盾兵（青色 tint + 头顶能量盾） */
  isGuardian?: boolean;
  /** 自定义贴图 tint（splitter mini 复用） */
  tint?: number;
  /** 非 Boss 时使用的贴图 key（默认 'mob-straw'） */
  textureKey?: string;
}

/**
 * Enemy —— 敌人
 *
 * 视觉：
 *   - 普通怪：'mob-*' 黄胖小怪贴图（textureKey 由 Spawner 随机选）
 *   - Boss：'mob-boss' 戴皇冠的胖大佬贴图，头顶仍叠 HP 比例条
 *
 * 动画：
 *   - 出场：spawnIn() 让贴图从 1.4 倍弹回 1 倍（轻微挤压）
 *   - 受击：在 takeDamage 内做左右抖动 + 闪一下
 *   - zigzag Boss：每帧根据 bossUpdate 里累计的 t 横向 sin 摆动
 */
export default class Enemy extends Phaser.GameObjects.Container {
  declare body: Phaser.Physics.Arcade.Body;

  hp: number;
  maxHp: number;
  scoreReward: number;
  isBoss: boolean;
  isMiniBoss: boolean;
  isElite: boolean;
  isShooter: boolean;
  isBomber: boolean;
  isGuardian: boolean;
  bossType: BossTypeKey;
  /** 死亡标记 —— 多个回调可能命中同一帧，靠这个去重 */
  _dead = false;

  private _sprite: Phaser.GameObjects.Image;
  /** 静止时贴图缩放（普通怪 = w/贴图宽，Boss = 1，miniBoss = mini 比例） */
  private _baseScale: number;
  private hpText: Phaser.GameObjects.Text;
  private _hpBar: Phaser.GameObjects.Rectangle | null = null;
  private _hpBarBg: Phaser.GameObjects.Rectangle | null = null;
  private _barFullW = 0;

  // Boss 主动攻击 / zigzag 状态
  private _attackAcc = 0;
  private _attackInterval: number = BOSS.attackInterval;
  private _timeAcc = 0;
  private _anchorX: number;
  private _zigzagAmp: number;
  private _zigzagFreq: number;
  /** Boss 当前阶段（1 = 普通；2 = 狂暴）。仅 isBoss && !isMiniBoss 会用 */
  private _phase: 1 | 2 = 1;
  /** 阶段切换回调（由 MainScene 注入用来播放横幅 / 音效） */
  onPhaseChange: ((boss: Enemy, phase: 1 | 2) => void) | null = null;

  // 射手精英开火状态
  private _shooterAcc = 0;

  // 自爆精英状态
  /** 已锁定（停下倒计时引爆中） */
  private _bomberLocked = false;
  /** 倒计时累计（lock 后开始计），>= ELITE_BOMBER.fuseMs 时引爆 */
  private _bomberFuseAcc = 0;
  /** 倒计时视觉：脉冲红光 */
  private _bomberPulse: Phaser.GameObjects.Arc | null = null;

  // 护盾兵：头顶弧形能量盾（视觉，闪一下表示挡伤）
  private _guardianShield: Phaser.GameObjects.Ellipse | null = null;

  // ── 冰冻 slow 状态 ────────────────────────────────────
  /** 减速倍率（1=正常，0.4=被冰冻）；持续到 _slowUntil */
  private _slowMul = 1;
  private _slowUntil = 0;
  /** 冰冻前的原始速度（解冻时直接还原，不依赖 ÷slowMul） */
  private _preSlowVx = 0;
  private _preSlowVy = 0;
  /** 冰冻视觉：覆盖在 sprite 上的青色半透明 overlay；slow 结束时移除 */
  private _slowOverlay: Phaser.GameObjects.Image | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: EnemyOptions = {}) {
    super(scene, x, y);
    scene.add.existing(this);
    scene.physics.add.existing(this);

    const w = opts.width ?? ENEMY.width;
    const h = opts.height ?? ENEMY.height;
    this.isBoss = !!opts.isBoss;
    this.isMiniBoss = !!opts.isMiniBoss;
    this.isElite = !!opts.isElite;
    this.isShooter = !!opts.isShooter;
    this.isBomber = !!opts.isBomber;
    this.isGuardian = !!opts.isGuardian;
    this.bossType = opts.bossType ?? 'standard';
    const bossCfg = BOSS_TYPES[this.bossType] ?? BOSS_TYPES.standard;

    // 投影 + 主体贴图
    const shadow = scene.add.ellipse(0, h / 2 - 4, w * 0.85, 8, 0x000000, 0.28);
    const tex = this.isBoss ? 'mob-boss' : (opts.textureKey ?? 'mob-straw');
    this._sprite = scene.add.image(0, 0, tex);

    // 计算 _baseScale：贴图源尺寸常常大于物理框，统一缩到与物理框等宽
    if (this.isMiniBoss) {
      this._baseScale = BOSS_TYPES.splitter.splitMiniSizeMul ?? 0.55;
    } else if (this.isBoss) {
      this._baseScale = 1; // Boss 贴图源尺寸 = BOSS.width/height，自然等于物理框
    } else {
      // 普通小怪：贴图源 110×80 → 物理框 ENEMY.width×height（默认 64×48）
      this._baseScale = w / this._sprite.width;
    }
    this._sprite.setScale(this._baseScale);

    if (this.isBoss && bossCfg.tint && bossCfg.tint !== 0xffffff) {
      this._sprite.setTint(bossCfg.tint);
    } else if (opts.tint) {
      this._sprite.setTint(opts.tint);
    } else if (this.isElite) {
      this._sprite.setTint(ELITE.tint);
    } else if (this.isShooter) {
      this._sprite.setTint(ELITE_SHOOTER.tint);
    } else if (this.isBomber) {
      this._sprite.setTint(ELITE_BOMBER.tint);
    } else if (this.isGuardian) {
      this._sprite.setTint(ELITE_GUARDIAN.tint);
    }
    this.add([shadow, this._sprite]);

    // 精英怪：脚下加一个紫色光环（呼吸动画）
    if (this.isElite) {
      const ring = scene.add.ellipse(0, h / 2 - 2, w * 1.05, 12, ELITE.tint, 0.45);
      ring.setStrokeStyle(2, ELITE.tint, 0.85);
      this.addAt(ring, 1);
      scene.tweens.add({
        targets: ring,
        scaleX: { from: 1, to: 1.15 },
        alpha: { from: 0.45, to: 0.7 },
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // 射手精英：脚下橙红色光环 + 头顶炮口光晕（呼吸动画）
    if (this.isShooter) {
      const ring = scene.add.ellipse(0, h / 2 - 2, w * 1.0, 10, ELITE_SHOOTER.tint, 0.45);
      ring.setStrokeStyle(2, ELITE_SHOOTER.tint, 0.85);
      this.addAt(ring, 1);
      scene.tweens.add({
        targets: ring,
        scaleX: { from: 1, to: 1.15 },
        alpha: { from: 0.45, to: 0.75 },
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      // 头顶炮口提示：一个朝下的小三角，告诉玩家"它会射东西"
      const muzzle = scene.add.triangle(
        0, h / 2 - 2,
        -5, 0,  5, 0,  0, 8,
        ELITE_SHOOTER.tint,
      );
      muzzle.setStrokeStyle(1.5, 0x000000, 0.6);
      this.add(muzzle);
    }

    // 自爆精英：脚下红色光环 + 头顶引线（视觉提示，会在 lock 后变成脉冲）
    if (this.isBomber) {
      const ring = scene.add.ellipse(0, h / 2 - 2, w * 1.05, 12, ELITE_BOMBER.tint, 0.5);
      ring.setStrokeStyle(2, ELITE_BOMBER.tint, 0.9);
      this.addAt(ring, 1);
      scene.tweens.add({
        targets: ring,
        scaleX: { from: 1, to: 1.2 },
        alpha: { from: 0.5, to: 0.85 },
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      // 头顶一团小红光（lock 时会替换成强脉冲）
      const fuse = scene.add.circle(0, -h / 2 - 6, 4, ELITE_BOMBER.tint, 1);
      fuse.setStrokeStyle(1, 0x000000, 0.5);
      this.add(fuse);
      // 引爆时升级成脉冲球
      this._bomberPulse = fuse;
    }

    // 护盾兵：头顶半圆形能量盾（用扁椭圆铺在头顶）
    if (this.isGuardian) {
      const shieldW = w * 1.15;
      const shieldH = h * 0.55;
      const shield = scene.add
        .ellipse(0, -h / 2 + 2, shieldW, shieldH, ELITE_GUARDIAN.shieldColor, 0.28)
        .setStrokeStyle(3, ELITE_GUARDIAN.shieldColor, 0.95);
      this.add(shield);
      this._guardianShield = shield;
      scene.tweens.add({
        targets: shield,
        alpha: { from: 0.95, to: 0.6 },
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Boss 头顶 HP 比例条
    if (this.isBoss) {
      const barW = w * 0.85;
      this._hpBarBg = scene.add.rectangle(0, -h / 2 - 14, barW, 7, 0x000000, 0.55);
      this._hpBar = scene.add
        .rectangle(-barW / 2, -h / 2 - 14, barW, 7, 0xff5577, 1)
        .setOrigin(0, 0.5);
      this.add([this._hpBarBg, this._hpBar]);
      this._barFullW = barW;
    }

    // HP 数字
    this.hp = opts.hp ?? ENEMY.baseHp;
    this.maxHp = this.hp;
    const fontSize = this.isBoss ? (this.isMiniBoss ? '22px' : '34px') : '20px';
    this.hpText = scene.add
      .text(0, this.isBoss ? 0 : h * 0.32, String(this.hp), {
        fontFamily: 'sans-serif',
        fontSize,
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: this.isBoss ? '#1a0606' : '#3a2008',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    this.add(this.hpText);

    this.body.setSize(w, h);
    this.body.setOffset(-w / 2, -h / 2);
    this.body.setAllowGravity(false);

    this.scoreReward = opts.score ?? ENEMY.score;

    this._anchorX = x;
    this._zigzagAmp = bossCfg.zigzagAmp;
    this._zigzagFreq = bossCfg.zigzagFreq;

    this._spawnIn();
  }

  private _spawnIn(): void {
    const baseScale = this._baseScale;
    // 出场弹性：普通怪 1.4× 横压扁；Boss 0.7× 拉竖
    this._sprite.setScale(
      this.isBoss ? baseScale * 0.7 : baseScale * 1.4,
      this.isBoss ? baseScale * 1.2 : baseScale * 0.7,
    );
    this.scene.tweens.add({
      targets: this._sprite,
      scaleX: baseScale,
      scaleY: baseScale,
      duration: 260,
      ease: 'Back.easeOut',
    });
  }

  /**
   * 由 MainScene.update 每帧调用（仅 Boss 有效）
   *
   * 大 Boss 行为：
   *   - 下落到屏幕高度 × hoverYRatio 处时刹车并悬停（mini Boss 不悬停，让分裂体能继续下压）
   *   - zigzag 类型在悬停后仍按 anchor + sin 摆动横向位置
   *   - standard / splitter（zigzagAmp=0）悬停后用 BOSS.hoverPatrol* 做小幅巡逻，避免站桩
   *   - 出场前（屏外）不开火；进入屏内后按 _attackInterval 周期攻击
   */
  bossUpdate(delta: number, onAttack: (boss: Enemy) => void): void {
    if (!this.isBoss || this._dead) return;
    this._tickSlow();
    this._timeAcc += delta;

    // 悬停（仅大 Boss）：靠近 hoverY 时减速，越过就硬钉住
    let isHovering = false;
    if (!this.isMiniBoss && this.body) {
      const hoverY = GAME_HEIGHT * BOSS.hoverYRatio;
      const dy = hoverY - this.y;
      if (dy <= 0) {
        // 已到位 / 越过：钉住 y，清掉下落速度
        this.y = hoverY;
        this.body.velocity.y = 0;
        isHovering = true;
      } else if (dy < BOSS.hoverBrakeRange) {
        // 接近悬停点：按距离比例线性减速到 0，避免到位瞬间急刹车
        const factor = dy / BOSS.hoverBrakeRange;
        if (this.body.velocity.y > 0) {
          this.body.velocity.y = Math.min(this.body.velocity.y, this.body.velocity.y * factor);
        }
      }
    }

    // 横向位移：
    //   - zigzag 类型用自己的大摆幅
    //   - 其它类型悬停后用 hoverPatrol 小摆幅，免得"站着挨打"
    if (this._zigzagAmp > 0 && this._zigzagFreq > 0) {
      const t = this._timeAcc / 1000;
      this.x = this._anchorX + Math.sin(t * this._zigzagFreq) * this._zigzagAmp;
    } else if (isHovering) {
      const t = this._timeAcc / 1000;
      this.x = this._anchorX + Math.sin(t * BOSS.hoverPatrolFreq) * BOSS.hoverPatrolAmp;
    }

    if (this.y < this.body.halfHeight + 8) return;
    this._attackAcc += delta;
    while (this._attackAcc >= this._attackInterval) {
      this._attackAcc -= this._attackInterval;
      onAttack(this);
    }
  }

  /**
   * 射手精英：周期性向玩家方向开火。由 MainScene.update 每帧调用。
   *   - 出场后 attackWarmup ms 才允许第一次开火（避免出生在屏外秒玩家）
   *   - 屏外（y < 0）也不开火
   *   - 节奏：每 attackInterval ms 一发
   */
  shooterUpdate(delta: number, onAttack: (shooter: Enemy) => void): void {
    if (!this.isShooter || this._dead || this.isBoss) return;
    this._shooterAcc += delta;
    if (this._shooterAcc < ELITE_SHOOTER.attackWarmup) return;
    if (this.y < 30) return;
    this._attackAcc += delta;
    while (this._attackAcc >= ELITE_SHOOTER.attackInterval) {
      this._attackAcc -= ELITE_SHOOTER.attackInterval;
      onAttack(this);
    }
  }

  /**
   * 自爆精英：每帧由 MainScene 调用
   *   - 未锁定：检测到玩家在 triggerRadius 内 → 锁定（停下 + 启动倒计时 + 颜色脉冲）
   *   - 已锁定：累计 fuseMs → 调 onExplode（由 MainScene 做 AOE 命中检测 + 杀掉自己）
   * 出场前（y < 0）不触发，避免在屏外锁死
   */
  bomberUpdate(delta: number, target: { x: number; y: number }, onExplode: (b: Enemy) => void): void {
    if (!this.isBomber || this._dead || this.isBoss) return;
    if (this.y < 30) return;

    if (!this._bomberLocked) {
      const dx = target.x - this.x;
      const dy = target.y - this.y;
      const r = ELITE_BOMBER.triggerRadius;
      if (dx * dx + dy * dy <= r * r) {
        this._bomberLocked = true;
        // 锁定：停下来
        if (this.body) this.body.setVelocity(0, 0);
        // 头顶引线脉冲（红光忽大忽小，让玩家清晰感到"要爆了"）
        if (this._bomberPulse) {
          this.scene.tweens.killTweensOf(this._bomberPulse);
          this.scene.tweens.add({
            targets: this._bomberPulse,
            scale: { from: 1, to: 2.2 },
            alpha: { from: 1, to: 0.3 },
            duration: 240,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        }
        // 自身整体红色闪烁
        this.scene.tweens.add({
          targets: this._sprite,
          alpha: { from: 1, to: 0.6 },
          duration: 180,
          yoyo: true,
          repeat: -1,
        });
      }
      return;
    }

    this._bomberFuseAcc += delta;
    if (this._bomberFuseAcc >= ELITE_BOMBER.fuseMs) {
      onExplode(this);
    }
  }

  /**
   * 冰冻：把速度按 slowMul 比例缩放；durationMs 后还原
   * - 缓存 _preSlowV{x,y} 作为"未减速的速度"，解冻时直接赋回
   *   （不能用 ÷slowMul：Boss hover 会把 vy 钉成 0，除以 0.6 还是 0；
   *    且穿透 / 链电多次命中也会让累乘出错）
   * - 已 slow 时：只续时间；倍率不再叠加
   * Boss 也会被减速，但减速倍率会被夹到 ≥0.6（避免 Boss 完全不动）
   */
  applySlow(slowMul: number, durationMs: number): void {
    if (this._dead) return;
    const eff = this.isBoss ? Math.max(0.6, slowMul) : slowMul;
    const now = performance.now();

    if (this._slowMul < 1) {
      // 已经在 slow 中：只续时间
      this._slowUntil = Math.max(this._slowUntil, now + durationMs);
      return;
    }

    if (this.body) {
      // 缓存原速作为解冻基准
      this._preSlowVx = this.body.velocity.x;
      this._preSlowVy = this.body.velocity.y;
      this.body.velocity.x *= eff;
      this.body.velocity.y *= eff;
    }
    this._slowMul = eff;
    this._slowUntil = now + durationMs;
    if (!this._slowOverlay) {
      const w = this.body ? this.body.width : 64;
      const h = this.body ? this.body.height : 48;
      // 用 particle (4×4 白) 做底色拉伸成椭圆
      const ov = this.scene.add.image(0, 0, 'particle')
        .setDisplaySize(w * 1.1, h * 1.1)
        .setTint(0x6ad6ff)
        .setAlpha(0.35);
      this.add(ov);
      this._slowOverlay = ov;
    }
  }

  /** 每帧检查 slow 是否已过期；过期就把速度还回去 + 移除 overlay */
  private _tickSlow(): void {
    if (this._slowMul >= 1) return;
    if (performance.now() < this._slowUntil) return;
    if (this.body) {
      // 直接还原到冻结前的速度，避免"÷ slowMul"在 Boss hover (vy=0) 时除回 0
      // 普通怪：解冻后 chaseUpdate 会立即把方向重新指向玩家
      this.body.velocity.x = this._preSlowVx;
      this.body.velocity.y = this._preSlowVy;
    }
    this._slowMul = 1;
    this._preSlowVx = 0;
    this._preSlowVy = 0;
    if (this._slowOverlay) {
      this._slowOverlay.destroy();
      this._slowOverlay = null;
    }
  }

  guardianAdjustDamage(rawDmg: number, hasPierce: boolean): number {
    if (!this.isGuardian) return rawDmg;
    if (hasPierce) return rawDmg;
    // 盾闪一下表示挡了
    if (this._guardianShield) {
      this.scene.tweens.killTweensOf(this._guardianShield);
      this.scene.tweens.add({
        targets: this._guardianShield,
        alpha: { from: 1, to: 0.4 },
        scaleX: { from: 1.15, to: 1 },
        scaleY: { from: 1.15, to: 1 },
        duration: 160,
        onComplete: () => {
          if (this._guardianShield) this._guardianShield.alpha = 0.85;
        },
      });
    }
    return rawDmg * ELITE_GUARDIAN.shieldDamageMul;
  }

  /**
   * 普通怪 / 精英怪 追击：每帧调用，让速度向量缓慢转向玩家。
   * - 速度模长保持不变（保留 Spawner 出生时给的下落速度）
   * - 受 ENEMY_CHASE.minVerticalRatio 约束：vy 不允许小于"基础下落速度 × 比例"，
   *   防止怪物绕开屏幕水平横扫，最终一定会到屏幕底
   * Boss / mini Boss 不走这条逻辑（它们有自己的 zigzag/锚点行为）
   */
  chaseUpdate(delta: number, target: { x: number; y: number }): void {
    if (this.isBoss || this._dead || !this.body) return;
    this._tickSlow();
    // 自爆兵锁定后不再追击（停下倒计时）
    if (this.isBomber && this._bomberLocked) return;
    const vx = this.body.velocity.x;
    const vy = this.body.velocity.y;
    const speed = Math.hypot(vx, vy);
    if (speed < 1) return; // 几乎不动就别折腾了

    const curAng = Math.atan2(vy, vx);
    const tgtAng = Math.atan2(target.y - this.y, target.x - this.x);
    let dAng = Phaser.Math.Angle.Wrap(tgtAng - curAng);
    const maxStep = (ENEMY_CHASE.homingTurnRate * delta) / 1000;
    dAng = Phaser.Math.Clamp(dAng, -maxStep, maxStep);
    let newAng = curAng + dAng;

    let nvx = Math.cos(newAng) * speed;
    let nvy = Math.sin(newAng) * speed;
    // vy 不能太小：保证怪物始终朝下推进
    const minVy = speed * ENEMY_CHASE.minVerticalRatio;
    if (nvy < minVy) {
      nvy = minVy;
      // 重算 vx 让总速度仍 = speed
      const remain = Math.max(0, speed * speed - nvy * nvy);
      nvx = Math.sign(nvx || (target.x - this.x)) * Math.sqrt(remain);
    }
    this.body.velocity.x = nvx;
    this.body.velocity.y = nvy;
  }

  /** 当前 Boss 阶段（mini Boss 永远 1） */
  get phase(): 1 | 2 { return this._phase; }

  /**
   * 扣血；返回是否死亡
   *
   * 防雪崩：单次伤害软上限 —— 超过 `maxHp × 0.5` 的部分按 0.4× 线性衰减。
   * 让堆叠词条仍有收益，但单发瞬秒怪物的极端组合被抑制（防 Combo 雪崩）。
   *
   * 例（maxHp 60，cap=30）：
   *   dmg 20  → 20（未触发）
   *   dmg 60  → 30 + 30 × 0.4 = 42
   *   dmg 200 → 30 + 170 × 0.4 = 98（仍能秒，只是没那么夸张）
   *
   * Boss / mini Boss（maxHp 千级）几乎不会触发；
   * 主要削小怪面对暴击 + Combo + 多重叠加时的"百伤 vs 60 血"超杀情况。
   */
  takeDamage(amount = 1): boolean {
    if (this._dead) return false;
    // 单次伤害软上限：超过 maxHp×0.5 的部分按 0.4× 线性衰减
    let dmg = Math.max(1, Math.round(amount));
    const cap = Math.max(2, this.maxHp * 0.5);
    if (dmg > cap) {
      dmg = Math.round(cap + (dmg - cap) * 0.4);
    }
    this.hp -= dmg;
    if (this.hp < 0) this.hp = 0;
    this.hpText.setText(String(this.hp));
    if (this._hpBar) {
      this._hpBar.width = this._barFullW * (this.hp / this.maxHp);
    }

    // Boss 二阶段：HP 跌破 phase2HpRatio 时狂暴一次
    if (this.isBoss && !this.isMiniBoss && this._phase === 1
        && this.hp > 0 && this.hp <= this.maxHp * BOSS.phase2HpRatio) {
      this._enterPhase2();
    }

    const sprite = this._sprite;
    this.scene.tweens.killTweensOf(sprite);
    sprite.x = 0;
    this.scene.tweens.add({
      targets: sprite,
      x: { from: -3, to: 3 },
      duration: 50,
      yoyo: true,
      repeat: 1,
      onComplete: () => { sprite.x = 0; },
    });
    this.scene.tweens.add({
      targets: sprite,
      alpha: { from: 1, to: 0.4 },
      duration: 40,
      yoyo: true,
      repeat: 1,
    });

    if (this.hp <= 0) {
      this._dead = true;
      return true;
    }
    return false;
  }

  /**
   * Boss 进入二阶段（狂暴）：
   *   - 攻击间隔变密、下落 / 横移加速
   *   - 自身闪一道白光 + 红色 tint 加深
   *   - HP 条变橙色（玩家一眼能看出"它已经狂暴了"）
   *   - zigzag 摆幅 + 频率也提一档（如果原来就是 zigzag）
   * 通过 onPhaseChange 让 MainScene 弹横幅 / 播音效
   */
  private _enterPhase2(): void {
    this._phase = 2;
    this._attackInterval = Math.max(180, Math.round(this._attackInterval * BOSS.phase2AttackIntervalMul));
    this._zigzagAmp = this._zigzagAmp * 1.2;
    this._zigzagFreq = this._zigzagFreq * 1.2;
    if (this.body) {
      this.body.velocity.x *= BOSS.phase2SpeedMul;
      this.body.velocity.y *= BOSS.phase2SpeedMul;
    }
    // HP 条改色（橙红，更醒目）
    if (this._hpBar) this._hpBar.fillColor = 0xff8a3c;
    // 全身白光闪一下，再加深 tint
    this.scene.tweens.add({
      targets: this._sprite,
      alpha: { from: 0.3, to: 1 },
      duration: 120,
      yoyo: true,
      repeat: 2,
    });
    // tint 叠红：偏暗的红 tint 让 boss 看上去像"上头了"
    this._sprite.setTint(0xff5566);
    this.onPhaseChange?.(this, 2);
  }
}
